import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, Plus, MoreVertical, Download, Trash2, Eye, CheckCircle, AlertCircle, Clock, FileText, Database, Upload } from 'lucide-react';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { StatCard } from '../components/StatCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Card } from '../components/SubscriptionAppCard';
// Mock Data
const MOCK_MIGRATIONS = [{
  id: '1',
  name: 'Recharge Subscriber Import',
  source: 'Recharge',
  records: 847,
  imported: 831,
  errors: 16,
  status: 'Completed',
  date: '12 Jan 2026'
}, {
  id: '2',
  name: 'WineHub Legacy Import',
  source: 'WineHub',
  records: 312,
  imported: 312,
  errors: 0,
  status: 'Completed',
  date: '15 Nov 2025'
}, {
  id: '3',
  name: 'Manual CSV Upload',
  source: 'CSV',
  records: 88,
  imported: 0,
  errors: 0,
  status: 'Draft',
  date: '10 Oct 2025'
}];
export function MigrationHubPage() {
  const navigate = useNavigate();
  const [isMigrating, setIsMigrating] = useState(false); // Toggle for demo
  const [migrations, setMigrations] = useState(MOCK_MIGRATIONS);
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'warning';
      case 'Draft':
        return 'info';
      case 'Failed':
        return 'error';
      default:
        return 'default';
    }
  };
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'Recharge':
        return <Database className="w-4 h-4 text-[#64748B]" data-id="element-11039" data-mp="3dtwqywwsgp2k" />;
      case 'WineHub':
        return <Database className="w-4 h-4 text-[#64748B]" data-id="element-11040" data-mp="3htov8xf5g924" />;
      case 'CSV':
        return <FileText className="w-4 h-4 text-[#64748B]" data-id="element-11041" data-mp="3muuqb1wa88et" />;
      default:
        return <Database className="w-4 h-4 text-[#64748B]" data-id="element-11042" data-mp="3rswhka3v3e1d" />;
    }
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-11043" data-mp="3rr1xsz4btryv">
      {/* NavBar */}
      <div className="mb-6" data-id="element-11044" data-mp="1rrokyxlf0a3b">
        <NavBar tabs={[]} title="Migration" data-id="element-11045" data-mp="1rotw8tvdc343" />
      </div>

      {/* Page Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-11046" data-mp="1mtn1wcnzoaru">
        <p className="text-sm text-[#64748B]" data-id="element-11047" data-mp="21iawafm3meg7">
          Import subscribers from other platforms and preserve their history.
        </p>
        <div className="flex items-center gap-3" data-id="element-11048" data-mp="2ahec9apyydqh">
          {/* Demo Toggle */}
          <label className="flex items-center gap-2 cursor-pointer mr-4" data-id="element-11049" data-mp="2ajha2gxd5b5r">
            <input type="checkbox" checked={isMigrating} onChange={e => setIsMigrating(e.target.checked)} className="w-4 h-4 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-11050" data-mp="26f9k6gf65p5y" />
            <span className="text-sm text-[#64748B]" data-id="element-11051" data-mp="2fhslgw3b3tq3">
              Demo: Show In Progress
            </span>
          </label>

          <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-11053" data-mp="l2q3rkki1fxq" />} onClick={() => navigate('/management/migration/new')} data-id="element-11052" data-mp="q3ni1m2e1pty">
            New Migration
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-11054" data-mp="31owms3hzxsr6">
        <StatCard label="Total Migrations" value="3" secondaryValue="all time" data-id="element-11055" data-mp="31qr1h02ocomt" />
        <StatCard label="Records Imported" value="1,247" secondaryValue="across all migrations" data-id="element-11056" data-mp="2mzqi400e3abw" />
        <StatCard label="Last Migration" value="12 Jan 2026" secondaryValue="Recharge Import" data-id="element-11057" data-mp="2rxa81x6g9bjo" />
        <StatCard label="Success Rate" value="98.2%" trend={{
        value: '',
        isPositive: true
      }} // Using trend for color only
      data-id="element-11058" data-mp="2dy0pwryoo7d2" />
      </div>

      {/* In Progress Alert Banner */}
      {isMigrating && <div className="mb-8 bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-11059" data-mp="243ijvg5snzq3">
          <div className="flex items-start gap-4" data-id="element-11060" data-mp="292bnmgva4cd2">
            <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0 animate-pulse" data-id="element-11061" data-mp="29jbb2k23wh3">
              <Upload className="w-5 h-5 text-[#0EA5E9]" data-id="element-11062" data-mp="78cea11pw530" />
            </div>
            <div className="flex-1" data-id="element-11063" data-mp="23az9ul62u95n">
              <div className="flex items-center justify-between mb-2" data-id="element-11064" data-mp="239wkihoekeru">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-11065" data-mp="1zbqg3gb1eba3">
                  Importing 847 records...
                </h3>
                <button onClick={() => navigate('/management/migration/1')} className="text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-11066" data-mp="1ud5gnj3lzvmp">
                  View Details
                </button>
              </div>
              <p className="text-sm text-[#64748B] mb-3" data-id="element-11067" data-mp="1fmogs46mhdrx">
                623 of 847 processed • ~4 min remaining
              </p>
              <div className="w-full bg-[#F1F5F9] rounded-full h-2 overflow-hidden" data-id="element-11068" data-mp="1blw410yjkqqw">
                <div className="bg-[#0EA5E9] h-2 rounded-full transition-all duration-500" style={{
              width: '73%'
            }} data-id="element-11069" data-mp="16mj8lrtlubcp" />
              </div>
            </div>
          </div>
        </div>}

      {/* Migration History Table */}
      {migrations.length > 0 ? <Card padding="none" className="overflow-hidden" data-id="element-11070" data-mp="mjnbroatb1d4">
          <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-11071" data-mp="mj3so49fe6xi">
            <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-11072" data-mp="rhx9a5k0ee1w">
              Migration History
            </h2>
          </div>
          <div className="overflow-x-auto" data-id="element-11073" data-mp="wicl2son7dhr">
            <table className="w-full" data-id="element-11074" data-mp="11gw5gonl8h80">
              <thead className="bg-[#F8FAFC]" data-id="element-11075" data-mp="11fut8guu5oop">
                <tr data-id="element-11076" data-mp="11ejskpwosx1t">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11077" data-mp="3tnsji1nkghm">
                    Migration Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11078" data-mp="8pvzw99lz5e8">
                    Source
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11079" data-mp="ijl859linxv7">
                    Records
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11080" data-mp="3m6bsr9nllnsw">
                    Imported
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11081" data-mp="397cekstak1cz">
                    Errors
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11082" data-mp="3nvgz82bwmmye">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11083" data-mp="2v8mjfiq18br9">
                    Date
                  </th>
                  <th className="px-6 py-3 w-10" data-id="element-11084" data-mp="355r4ff85616h"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-11085" data-mp="306o2i3ngkb2q">
                {migrations.map(migration => <tr key={migration.id} className="hover:bg-[#F8FAFC]" data-id="element-11086" data-mp="3090qoyjt7ho8">
                    <td className="px-6 py-4" data-id="element-11087" data-mp="kdp269iwvctc">
                      <button onClick={() => navigate(`/management/migration/${migration.id}`)} className="text-sm font-medium text-[#0F172A] hover:text-[#0EA5E9] hover:underline text-left" data-id="element-11088" data-mp="fgp4on04nidu">
                        {migration.name}
                      </button>
                    </td>
                    <td className="px-6 py-4" data-id="element-11089" data-mp="dz9tf2oqiih1">
                      <div className="flex items-center gap-2" data-id="element-11090" data-mp="e0l47mglkqhn">
                        {getSourceIcon(migration.source)}
                        <span className="text-sm text-[#475569]" data-id="element-11091" data-mp="i2xz1ftt33zn">
                          {migration.source}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-[#475569]" data-id="element-11092" data-mp="1aor3ruynwsp2">
                      {migration.records}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-[#475569]" data-id="element-11093" data-mp="10z7kio71ewto">
                      {migration.imported}
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-11094" data-mp="1oo0kcvqs5otk">
                      <span className={`text-sm font-medium ${migration.errors > 0 ? 'text-[#EF4444]' : 'text-[#94A3B8]'}`} data-id="element-11095" data-mp="1tga6s1sos6r3">
                        {migration.errors}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-11096" data-mp="zxvzq6t62i70">
                      <Badge variant={getStatusBadgeVariant(migration.status)} data-id="element-11097" data-mp="uzkq9x6sa9pb">
                        {migration.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-11098" data-mp="pydhbuswn4h2">
                      {migration.date}
                    </td>
                    <td className="px-6 py-4" data-id="element-11099" data-mp="1ipf2ccja3u8i">
                      <button className="text-[#94A3B8] hover:text-[#0F172A] transition-colors" data-id="element-11100" data-mp="1ipor5wsl7vcm">
                        <MoreVertical className="w-4 h-4" data-id="element-11101" data-mp="1dohky263f482" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-between" data-id="element-11102" data-mp="28sx78l0m167">
            <span className="text-sm text-[#64748B]" data-id="element-11103" data-mp="3tjb5t9i01sl0">
              Showing 1-{migrations.length} of {migrations.length} migrations
            </span>
            <div className="flex gap-2" data-id="element-11104" data-mp="2dxh6w00l1wr4">
              <Button variant="secondary" size="sm" disabled data-id="element-11105" data-mp="2dw66892fp548">
                Previous
              </Button>
              <Button variant="secondary" size="sm" disabled data-id="element-11106" data-mp="2ivit2ttb2c6t">
                Next
              </Button>
            </div>
          </div>
        </Card> /* Empty State */ : <div className="flex flex-col items-center justify-center py-16 bg-white border border-[#E2E8F0] rounded-lg shadow-sm" data-id="element-11107" data-mp="2smdfpzagrdss">
          <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mb-4" data-id="element-11108" data-mp="2wqtj9twlu94u">
            <ArrowRightLeft className="w-8 h-8 text-[#94A3B8]" data-id="element-11109" data-mp="31m0hwnb0onmw" />
          </div>
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2" data-id="element-11110" data-mp="36m4zyhrkpqf8">
            No migrations yet
          </h3>
          <p className="text-sm text-[#64748B] max-w-md text-center mb-6" data-id="element-11111" data-mp="17bwxiar3o40d">
            Import your existing subscribers from another platform. We'll
            preserve their join dates, order history, and tenure so your
            analytics work from day one.
          </p>
          <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-11113" data-mp="7r6qqatohgx5" />} onClick={() => navigate('/management/migration/new')} data-id="element-11112" data-mp="2slzh0wem7ku">
            Start Your First Migration
          </Button>
        </div>}
    </div>;
}